/**
 * Turn a saved pack (GET /api/admin/v2/marketplace/packs/<id>/) back into
 * the composer-wizard state it was built from.
 *
 * Why this needs `source_id`: pack template rows (PackTeam / PackPlayer /
 * PackUmpire / PackVenue) are snapshots copied out of the coredata
 * catalog, with their own fresh UUIDs. The wizards work in *catalog* ids,
 * so re-opening a draft means mapping each template row back to the
 * catalog row it came from. That link is `source_id`, added 2026-08-03
 * and backfilled by marketplace migration 0008.
 *
 * Rows whose `source_id` is null could not be traced back (the catalog
 * row was renamed, deleted, or is ambiguous). They are returned in
 * `unmatched` so the wizard can name them for the operator instead of
 * dropping them silently — saving re-runs select-*, which wipes and
 * rewrites the pack, so an unmatched row is lost unless it is re-picked.
 */

export function detailsFromPack(pack) {
  return {
    name: pack.name || "",
    season: pack.season || "",
    short_description: pack.short_description || "",
    description: pack.description || "",
    price_credits: pack.price_credits ?? 0,
  };
}

function playerLabel(p) {
  const parts = [p.first_name, p.middle_name, p.last_name].filter(Boolean);
  return p.nick_name || parts.join(" ").trim() || "(unnamed)";
}

/**
 * SQUAD_PACK. Returns catalog-shaped rows so the existing renderRow /
 * renderChip callbacks work unchanged against hydrated data.
 */
export function hydrateSquad(pack) {
  const packPlayers = pack.players || [];
  const packTeams = pack.teams || [];
  const playerByPackId = {};
  for (const p of packPlayers) playerByPackId[String(p.id)] = p;

  const teamIds = [];
  const teamById = {};
  const playerIdsByTeam = {};
  const seedTeams = [];
  const seedPlayers = [];
  const unmatched = [];

  for (const pt of packTeams) {
    if (!pt.source_id) {
      unmatched.push(`Team “${pt.name}”`);
      continue;
    }
    const tid = String(pt.source_id);
    teamIds.push(tid);

    const roster = [];
    for (const packPlayerId of pt.master_squad || []) {
      const pp = playerByPackId[String(packPlayerId)];
      if (!pp) continue;
      if (!pp.source_id) {
        unmatched.push(`${playerLabel(pp)} (${pt.short_name || pt.name})`);
        continue;
      }
      const pid = String(pp.source_id);
      roster.push(pid);
      seedPlayers.push({
        id: pid,
        first_name: pp.first_name,
        middle_name: pp.middle_name,
        last_name: pp.last_name,
        nick_name: pp.nick_name,
        role: pp.role,
        gender: pp.gender,
        nationality_name: pp.nationality_name,
      });
    }

    playerIdsByTeam[tid] = roster;
    // master_squad_ids is seeded with the roster as saved, so the card's
    // Reset button reverts to the saved draft rather than to the catalog's
    // current master squad (which we haven't fetched). The wizard relabels
    // that button in edit mode so the behaviour matches what it says.
    const row = {
      id: tid,
      name: pt.name,
      short_name: pt.short_name,
      team_type: pt.team_type,
      nationality_name: pt.nationality_name,
      master_squad_ids: roster,
    };
    teamById[tid] = row;
    seedTeams.push(row);
  }

  return { teamIds, teamById, playerIdsByTeam, seedTeams, seedPlayers, unmatched };
}

/** OFFICIALS_PACK — flat list of umpire ids. */
export function hydrateOfficials(pack) {
  const ids = [];
  const seedRows = [];
  const unmatched = [];
  for (const pu of pack.umpires || []) {
    if (!pu.source_id) {
      unmatched.push([pu.name, pu.last_name].filter(Boolean).join(" "));
      continue;
    }
    const id = String(pu.source_id);
    ids.push(id);
    seedRows.push({
      id,
      name: pu.name,
      middle_name: pu.middle_name,
      last_name: pu.last_name,
      short_name: pu.short_name,
      role: pu.role,
      city: pu.city,
      state: pu.state,
      nationality_name: pu.nationality_name,
    });
  }
  return { ids, seedRows, unmatched };
}

/** VENUE_PACK — flat list of stadium ids. */
export function hydrateVenues(pack) {
  const ids = [];
  const seedRows = [];
  const unmatched = [];
  for (const pv of pack.venues || []) {
    if (!pv.source_id) {
      unmatched.push([pv.name, pv.city].filter(Boolean).join(", "));
      continue;
    }
    const id = String(pv.source_id);
    ids.push(id);
    seedRows.push({
      id,
      name: pv.name,
      city: pv.city,
      state: pv.state,
      specific_area: pv.specific_area,
      established_year: pv.established_year,
      floodlights: pv.floodlights,
      nationality_name: pv.nationality_name,
    });
  }
  return { ids, seedRows, unmatched };
}
